import { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const empty = {
  name: "",
  client_id: "",
  site_address: "",
  site_lat: 0,
  site_lng: 0,
  gps_radius_meters: 200,
  gps_enforcement_enabled: true,
  daily_hour_limit: 8,
  include_saturdays_as_holidays: false,
  monthly_cl_grant: 1,
  cl_cycle_start_month: 1,
  cl_cycle_end_month: 12,
  half_day_enabled: true,
  half_day_threshold_hours: 6,
  sandwich_leave_enabled: true,
  late_tracking_enabled: false,
  start_time_hhmm: "09:30",
  grace_cutoff_hhmm: "10:15",
  monthly_free_late_limit: 2,
  late_penalty_half_day: true,
  start_date: "",
  end_date: "",
};

export default function Projects() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/projects"), api.get("/clients")]);
    setRows(p.data);
    setClients(c.data);
  };
  useEffect(() => {
    load();
  }, []);

  const captureMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          site_lat: pos.coords.latitude,
          site_lng: pos.coords.longitude,
        }));
        toast.success("Location captured");
      },
      (err) => toast.error(err.message || "Failed to capture location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        site_lat: parseFloat(form.site_lat),
        site_lng: parseFloat(form.site_lng),
        gps_radius_meters: parseInt(form.gps_radius_meters || 200),
        gps_enforcement_enabled: !!form.gps_enforcement_enabled,
        daily_hour_limit: parseFloat(form.daily_hour_limit || 8),
        include_saturdays_as_holidays: !!form.include_saturdays_as_holidays,
        monthly_cl_grant: parseInt(form.monthly_cl_grant || 1),
        cl_cycle_start_month: parseInt(form.cl_cycle_start_month || 1),
        cl_cycle_end_month: parseInt(form.cl_cycle_end_month || 12),
        half_day_enabled: !!form.half_day_enabled,
        half_day_threshold_hours: parseFloat(form.half_day_threshold_hours || 6),
        sandwich_leave_enabled: !!form.sandwich_leave_enabled,
        late_tracking_enabled: !!form.late_tracking_enabled,
        start_time_hhmm: form.start_time_hhmm || "09:30",
        grace_cutoff_hhmm: form.grace_cutoff_hhmm || "10:15",
        monthly_free_late_limit: parseInt(form.monthly_free_late_limit ?? 2),
        late_penalty_half_day: !!form.late_penalty_half_day,
      };
      if (editing) {
        await api.put(`/projects/${editing}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const edit = (row) => {
    setForm({
      name: row.name,
      client_id: row.client_id,
      site_address: row.site_address || "",
      site_lat: row.site_lat,
      site_lng: row.site_lng,
      gps_radius_meters: row.gps_radius_meters || 200,
      gps_enforcement_enabled: row.gps_enforcement_enabled !== false,
      daily_hour_limit: row.daily_hour_limit ?? 8,
      include_saturdays_as_holidays: !!row.include_saturdays_as_holidays,
      monthly_cl_grant: row.monthly_cl_grant ?? 1,
      cl_cycle_start_month: row.cl_cycle_start_month ?? 1,
      cl_cycle_end_month: row.cl_cycle_end_month ?? 12,
      half_day_enabled: row.half_day_enabled !== false,
      half_day_threshold_hours: row.half_day_threshold_hours ?? 6,
      sandwich_leave_enabled: row.sandwich_leave_enabled !== false,
      late_tracking_enabled: !!row.late_tracking_enabled,
      start_time_hhmm: row.start_time_hhmm || "09:30",
      grace_cutoff_hhmm: row.grace_cutoff_hhmm || "10:15",
      monthly_free_late_limit: row.monthly_free_late_limit ?? 2,
      late_penalty_half_day: row.late_penalty_half_day !== false,
      start_date: row.start_date || "",
      end_date: row.end_date || "",
    });
    setEditing(row.id);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif font-bold text-2xl text-[#1A3A5C]">Projects</h2>
          <p className="text-sm text-[#475569]">Worksites with GPS verification</p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) {
              setEditing(null);
              setForm(empty);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button
              data-testid="new-project-btn"
              className="bg-[#1A3A5C] hover:bg-[#0F2236]"
              disabled={clients.length === 0}
            >
              <Plus className="w-4 h-4 mr-1" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif">
                {editing ? "Edit Project" : "New Project"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3 max-h-[75vh] overflow-y-auto pr-2">
              <div>
                <Label>Project Name *</Label>
                <Input
                  data-testid="project-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Client *</Label>
                <Select
                  value={form.client_id}
                  onValueChange={(v) => setForm({ ...form, client_id: v })}
                >
                  <SelectTrigger data-testid="project-client">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site Address</Label>
                <Textarea
                  rows={2}
                  value={form.site_address}
                  onChange={(e) =>
                    setForm({ ...form, site_address: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Latitude *</Label>
                  <Input
                    type="number"
                    step="any"
                    required
                    value={form.site_lat}
                    onChange={(e) =>
                      setForm({ ...form, site_lat: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Longitude *</Label>
                  <Input
                    type="number"
                    step="any"
                    required
                    value={form.site_lng}
                    onChange={(e) =>
                      setForm({ ...form, site_lng: e.target.value })
                    }
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={captureMyLocation}
                className="w-full"
              >
                <MapPin className="w-4 h-4 mr-1" /> Use My Current Location
              </Button>
              <div>
                <Label>GPS Radius (meters)</Label>
                <Input
                  type="number"
                  value={form.gps_radius_meters}
                  onChange={(e) =>
                    setForm({ ...form, gps_radius_meters: e.target.value })
                  }
                />
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border">
                <div>
                  <div className="text-sm font-semibold text-[#1A3A5C]">
                    Enforce GPS Radius
                  </div>
                  <div className="text-xs text-[#475569]">
                    If off, employees can mark present from anywhere
                  </div>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    data-testid="gps-enforcement-toggle"
                    checked={form.gps_enforcement_enabled}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        gps_enforcement_enabled: e.target.checked,
                      })
                    }
                  />
                  <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-[#22C55E] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Daily Working-Hour Limit</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    data-testid="daily-hour-limit"
                    value={form.daily_hour_limit}
                    onChange={(e) =>
                      setForm({ ...form, daily_hour_limit: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Monthly CL Grant</Label>
                  <Input
                    type="number"
                    min="0"
                    data-testid="monthly-cl-grant"
                    value={form.monthly_cl_grant}
                    onChange={(e) =>
                      setForm({ ...form, monthly_cl_grant: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Half-Day Hours Threshold</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    data-testid="half-day-threshold"
                    value={form.half_day_threshold_hours}
                    disabled={!form.half_day_enabled}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        half_day_threshold_hours: e.target.value,
                      })
                    }
                  />
                  <p className="text-xs text-[#475569] mt-1">
                    Hours less than this counts as 0.5 day
                  </p>
                </div>
                <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border">
                  <div>
                    <div className="text-sm font-semibold text-[#1A3A5C]">
                      Half-Day Leave
                    </div>
                    <div className="text-xs text-[#475569]">
                      Enable 0.5-day rule
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      data-testid="half-day-toggle"
                      checked={form.half_day_enabled}
                      onChange={(e) =>
                        setForm({ ...form, half_day_enabled: e.target.checked })
                      }
                    />
                    <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-[#F0A830] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border">
                <div>
                  <div className="text-sm font-semibold text-[#1A3A5C]">
                    Include Saturdays as holidays
                  </div>
                  <div className="text-xs text-[#475569]">
                    Sundays are always holidays
                  </div>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    data-testid="saturday-toggle"
                    checked={form.include_saturdays_as_holidays}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        include_saturdays_as_holidays: e.target.checked,
                      })
                    }
                  />
                  <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-[#A855F7] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                </label>
              </div>
              <div className="hidden">
                <Label>Monthly Casual Leave (CL) Grant — Legacy</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.monthly_cl_grant}
                  onChange={(e) =>
                    setForm({ ...form, monthly_cl_grant: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="md:col-span-2 flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border">
                  <div>
                    <div className="text-sm font-semibold text-[#1A3A5C]">
                      Sandwich Leave Policy
                    </div>
                    <div className="text-xs text-[#475569]">
                      Holidays between two absent days are also marked absent
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      data-testid="sandwich-toggle"
                      checked={form.sandwich_leave_enabled}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          sandwich_leave_enabled: e.target.checked,
                        })
                      }
                    />
                    <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-[#EF4444] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                  </label>
                </div>

                <div className="col-span-2 pt-2 border-t mt-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1A3A5C] mb-1">
                    Casual Leave (CL) Cycle Configuration
                  </div>
                  <div className="text-xs text-[#475569] mb-3">
                    Define the start and end month for the yearly casual leave cycle. Unused leaves carry forward within the cycle and reset to 0 at the start of a new cycle.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>CL Cycle Start Month</Label>
                      <Select
                        value={String(form.cl_cycle_start_month || 1)}
                        onValueChange={(v) =>
                          setForm({ ...form, cl_cycle_start_month: parseInt(v) })
                        }
                      >
                        <SelectTrigger data-testid="cl-cycle-start-month">
                          <SelectValue placeholder="Start Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => (
                            <SelectItem key={m.value} value={String(m.value)}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>CL Cycle End Month</Label>
                      <Select
                        value={String(form.cl_cycle_end_month || 12)}
                        onValueChange={(v) =>
                          setForm({ ...form, cl_cycle_end_month: parseInt(v) })
                        }
                      >
                        <SelectTrigger data-testid="cl-cycle-end-month">
                          <SelectValue placeholder="End Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => (
                            <SelectItem key={m.value} value={String(m.value)}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="col-span-2 pt-2 border-t mt-1">
                  <div className="flex items-center justify-between bg-amber-50/60 rounded-lg p-3 border border-amber-200 mb-3">
                    <div>
                      <div className="text-sm font-semibold text-[#1A3A5C]">
                        Late Attendance Policy
                      </div>
                      <div className="text-xs text-[#475569]">
                        Grace cut-off time, monthly free late limit & automatic half-day penalty rules
                      </div>
                    </div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        data-testid="late-tracking-toggle"
                        checked={form.late_tracking_enabled}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            late_tracking_enabled: e.target.checked,
                          })
                        }
                      />
                      <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-[#F59E0B] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                    </label>
                  </div>

                  {form.late_tracking_enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border">
                      <div>
                        <Label>Office Start Time</Label>
                        <Input
                          type="time"
                          data-testid="start-time"
                          value={form.start_time_hhmm}
                          onChange={(e) =>
                            setForm({ ...form, start_time_hhmm: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Label>Grace Cut-off Time</Label>
                        <Input
                          type="time"
                          data-testid="grace-cutoff-time"
                          value={form.grace_cutoff_hhmm}
                          onChange={(e) =>
                            setForm({ ...form, grace_cutoff_hhmm: e.target.value })
                          }
                        />
                        <p className="text-xs text-[#475569] mt-1">
                          Punch-ins after this time are marked late
                        </p>
                      </div>
                      <div>
                        <Label>Monthly Free Late Limit</Label>
                        <Input
                          type="number"
                          min="0"
                          data-testid="monthly-free-late-limit"
                          value={form.monthly_free_late_limit}
                          onChange={(e) =>
                            setForm({ ...form, monthly_free_late_limit: e.target.value })
                          }
                        />
                        <p className="text-xs text-[#475569] mt-1">
                          Allowed late punch-ins per month without penalty
                        </p>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-lg p-2.5 border mt-6">
                        <div>
                          <div className="text-xs font-semibold text-[#1A3A5C]">
                            Auto Half-Day Penalty Rule
                          </div>
                          <div className="text-[11px] text-[#475569]">
                            Mark Half-Day after exceeding limit
                          </div>
                        </div>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            data-testid="late-penalty-toggle"
                            checked={form.late_penalty_half_day}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                late_penalty_half_day: e.target.checked,
                              })
                            }
                          />
                          <div className="w-9 h-5 bg-slate-300 rounded-full peer peer-checked:bg-[#EF4444] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4"></div>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) =>
                      setForm({ ...form, start_date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) =>
                      setForm({ ...form, end_date: e.target.value })
                    }
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="save-project"
                className="w-full bg-[#1A3A5C] hover:bg-[#0F2236]"
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {clients.length === 0 && (
        <Card className="p-6 bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-900">
            Please add a client first before creating projects.
          </p>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="px-4 pt-3 pb-2 border-b bg-slate-50/50 flex items-center justify-between gap-3">
          <Input
            data-testid="projects-search"
            placeholder="Search by project, client, address…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
          />
          <div className="text-xs text-[#475569]">
            {(() => {
              const ql = q.trim().toLowerCase();
              const c = ql
                ? rows.filter((r) =>
                    [r.name, r.client_name, r.site_address]
                      .join(" ").toLowerCase().includes(ql)
                  ).length
                : rows.length;
              return `${c} / ${rows.length}`;
            })()}
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-[#475569]">
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Site</th>
              <th className="px-4 py-3">Radius</th>
              <th className="px-4 py-3 text-center">Active Emp</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-12 text-center text-[#475569]">
                  No projects yet.
                </td>
              </tr>
            )}
            {(() => {
              const ql = q.trim().toLowerCase();
              const filtered = ql
                ? rows.filter((r) =>
                    [r.name, r.client_name, r.site_address]
                      .join(" ").toLowerCase().includes(ql)
                  )
                : rows;
              if (rows.length > 0 && filtered.length === 0) {
                return (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-[#475569]">
                      No results for "{q}".
                    </td>
                  </tr>
                );
              }
              return filtered.map((r) => (
              <tr key={r.id} data-testid={`project-row-${r.id}`}>
                <td className="px-4 py-3 font-semibold text-[#1A3A5C]">{r.name}</td>
                <td className="px-4 py-3 text-sm">{r.client_name}</td>
                <td className="px-4 py-3 text-xs text-[#475569]">
                  {r.site_lat?.toFixed?.(4)}, {r.site_lng?.toFixed?.(4)}
                </td>
                <td className="px-4 py-3 text-sm">
                  {r.gps_radius_meters}m{" "}
                  {r.gps_enforcement_enabled === false && (
                    <span className="ml-1 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                      OFF
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    data-testid={`active-emp-${r.id}`}
                    className="inline-block bg-[#22C55E]/10 text-[#15803D] font-bold text-sm rounded-full px-2.5 py-0.5"
                  >
                    {r.active_workers ?? 0}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => edit(r)}
                    className="text-[#1A3A5C] hover:bg-slate-100 p-2 rounded"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    className="text-red-600 hover:bg-red-50 p-2 rounded ml-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
              ));
            })()}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
